<template>
  <section class="section tw-bg-white">
    <!-- Loading overlay -->
    <div v-if="isExporting" class="loading-overlay">
      <div class="loading-content">
        <i class="fa fa-spinner fa-spin fa-2x"></i>
        <p>Generating PDF...</p>
      </div>
    </div>

    <div class="columns is-centered mt-3 no-print">
      <div class="column is-narrow">
        <button class="button is-info is-small" @click="exportToCsv">
          <span class="icon">
            <i class="fa fa-download" />
          </span>
          <span>Export Analytics Table To CSV</span>
        </button>
        <button class="button is-success is-small" @click="exportToPdf">
          <span class="icon"><i class="fa fa-file-pdf" /></span>
          <span>Export Analytics to PDF</span>
        </button>
      </div>
    </div>
    
    <!-- table of analysis results header - this will be cloned for each page in the PDF -->
    <div class="tw-flex has-text-grey mb-4 pb-2 header-section">
      <div class="tw-w-48">
        <p class="heading">
          Analysis
        </p>
      </div>

      <!-- Heading for Current Portfolio -->
      <div
        v-if="checkupStore.portfolio?.isChecked"
        class="tw-basis-0 tw-grow has-text-right"
      >
        <p class="heading">
          {{ truncateName(checkupStore.portfolio.name) }}
        </p>
      </div>

      <!-- Heading for Benchmark Portfolio -->
      <div
        v-if="checkupStore.benchmark?.isChecked"
        class="tw-basis-0 tw-grow has-text-right"
      >
        <p class="heading">
          {{ truncateName(checkupStore.benchmark.name) }}
        </p>
      </div>

      <!-- Heading for Optimized Portfolio -->
      <div
        v-if="checkupStore.optimizedPortfolio?.isChecked"
        class="tw-basis-0 tw-grow has-text-right"
      >
        <p class="heading">
          {{ truncateName(checkupStore.optimizedPortfolio.name) }}
        </p>
      </div>

      <!-- Heading for Difference Column -->
      <div
        v-if="checkupStore.portfolio?.isChecked && checkupStore.benchmark?.isChecked"
        class="tw-basis-0 tw-grow has-text-right"
      >
        <p class="heading">
          Difference
        </p>
      </div>
    </div><!-- columns -->
    <hr class="mt-0 pt-0">

    <!-- Rows -->
    <div
      v-for="area in areas"
      v-if="area.current && area.current.length > 0"
      :key="area.name"
      class="area-container"
    >
      <div class="tw-flex">
        <!-- Area Name -->
        <div class="tw-w-48">
          <div class="tw-flex tw-items-center">
            <span class="tw-text-2xl tw-font-bold tw-text-gray-800 tw-pr-4">{{ area.name }}</span>
          </div>
        </div>

        <!-- Value for Current Portfolio -->
        <div
          v-if="checkupStore.portfolio?.isChecked"
          class="tw-basis-0 tw-grow tw-flex tw-flex-col tw-gap-1"
        >
          <div
            v-for="(item, i) in area.current"
            :key="item.name || i"
          >
            <div class="tw-flex tw-justify-between tw-gap-3">
              <div class="">
                <strong>{{ item.name }}</strong>
              </div>
              <div class="">
                <span
                  class="is-family-code has-text-weight-bold"
                  :style="{ color: advisorStore.firmAdvisorCoreSettings.portfolio_color }"
                >
                    <span v-if="isNumber(item.value)">{{ item.value?.toFixed(2) || '0.00' }}<span v-if="area.isPercent && !['Avg # Hldgs (funds)', 'Debt to Equity', 'Forward PE Ratio'].includes(item.name)">%</span></span>
                  <span v-else>{{ item.value }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Value for Benchmark Portfolio -->
        <div
          v-if="checkupStore.benchmark?.isChecked"
          class="tw-basis-0 tw-grow tw-flex tw-flex-col tw-gap-1"
        >
          <div
            v-for="(item, i) in area.proposal"
            :key="item.name || i"
          >
            <div class="tw-flex tw-justify-between tw-gap-3">
              <div class="">
                <strong :style="{visibility: 'hidden'}">{{ item.name }}</strong><!-- nice hack! such that the rows are aligned -->
              </div>
              <div class="">
                <span
                  class="is-family-code has-text-weight-bold"
                  :style="{ color: advisorStore.firmAdvisorCoreSettings.benchmark_color }"
                >
                    <span v-if="isNumber(item.value)">{{ item.value?.toFixed(2) || '0.00' }}<span v-if="area.isPercent && !['Avg # Hldgs (funds)', 'Debt to Equity', 'Forward PE Ratio'].includes(item.name)">%</span></span>
                  <span v-else>{{ item.value }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Value for Optimized Portfolio -->
        <div
          v-if="checkupStore.optimizedPortfolio?.isChecked"
          class="tw-basis-0 tw-grow tw-flex tw-flex-col tw-gap-1"
        >
          <div
            v-for="(item, i) in area.optimized"
            :key="item.name || i"
          >
            <div class="tw-flex tw-justify-between tw-gap-3">
              <div class="">
                <strong :style="{visibility: 'hidden'}">{{ item.name }}</strong><!-- nice hack! such that the rows are aligned -->
              </div>
              <div class="">
                <span
                  class="is-family-code has-text-weight-bold"
                  :style="{ color: advisorStore.firmAdvisorCoreSettings.proposed_color }"
                >
                    <span v-if="isNumber(item.value)">{{ item.value?.toFixed(2) || '0.00' }}<span v-if="area.isPercent && !['Avg # Hldgs (funds)', 'Debt to Equity', 'Forward PE Ratio'].includes(item.name)">%</span></span>
                  <span v-else>{{ item.value }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Difference between Portfolio and Benchmark -->
        <div
          v-if="checkupStore.portfolio?.isChecked && checkupStore.benchmark?.isChecked"
          class="tw-basis-0 tw-grow tw-flex tw-flex-col tw-gap-1"
        >
          <div
            v-for="(item, i) in area.proposal"
            :key="`diff-${item.name}${i}`"
          >
            <div class="tw-flex tw-justify-between tw-gap-3">
              <div class="">
                <strong :style="{visibility: 'hidden'}">{{ item.name }}</strong><!-- nice hack! such that the rows are aligned -->
              </div>
              <div class="">
                <span
                  class="is-family-code has-text-weight-bold"
                  :style="{ color: PORTFOLIO_COLORS.difference }"
                >
                  <span>{{ calculateDifference(area.current[i]?.value, item.value, area.isPercent, item.name) }}</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr class="mt-2 pt-0 mb-6">
    </div>
  </section>
</template>

<script>
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { GRADE_COLORS, PORTFOLIO_COLORS } from '@frontend/ui/features/checkup/helpers/checkupFunctions.js';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export default {
  name: 'OverviewArea',
  components: { },
  props: {
    areas: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      GRADE_COLORS,
      PORTFOLIO_COLORS,
      isExporting: false,
    };
  },
  computed: {
    ...mapStores(useCheckupStore, useAdvisorStore),
  },
  methods: {
    isNumber(value) {
      if (typeof value === 'number') return true;
      if (typeof value === 'string') {
        const num = Number(value);
        return !Number.isNaN(num);
      }
      return false;
    },
    truncateName(name) {
      if (name.length > 60) {
        return name.substring(0, 60) + '...';
      }
      return name;
    },
    exportToCsv() {
      const rows = [
        // Header row with general columns and new "Difference" column
        ['Analysis', 'Label'],
      ];

      if (this.checkupStore.portfolio) {
        rows[0].push(this.checkupStore.portfolio.name);
      }
      if (this.checkupStore.benchmark) {
        rows[0].push(this.checkupStore.benchmark.name);
      }
      if (this.checkupStore.optimizedPortfolio) {
        rows[0].push(this.checkupStore.optimizedPortfolio.name);
      }
      if (this.checkupStore.portfolio && this.checkupStore.benchmark) {
        rows[0].push('Difference between current portfolio and benchmark');
      }

      this.areas.forEach(area => {
        const maxEntries = Math.max(
            area.current?.length || 0,
            area.proposal?.length || 0,
            area.optimized?.length || 0,
        );

        for (let i = 0; i < maxEntries; i++) {
          const row = [
            i === 0 ? area.name : '',
            (area.current?.[i]?.name || area.proposal?.[i]?.name || area.optimized?.[i]?.name || 'N/A'),
          ];

          if (this.checkupStore.portfolio) {
            row.push(this.formatValue(area.current?.[i]?.value, area.isPercent, area.current?.[i]?.name));
          }
          if (this.checkupStore.benchmark) {
            row.push(this.formatValue(area.proposal?.[i]?.value, area.isPercent, area.proposal?.[i]?.name));
          }
          if (this.checkupStore.optimizedPortfolio) {
            row.push(this.formatValue(area.optimized?.[i]?.value, area.isPercent, area.optimized?.[i]?.name));
          }
          if (this.checkupStore.portfolio && this.checkupStore.benchmark) {
            row.push(this.calculateDifference(area.current?.[i]?.value, area.proposal?.[i]?.value, area.isPercent, area.current?.[i]?.name));
          }

          rows.push(row);
        }
      });

      const csvContent = rows.map(e => e.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', 'export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    formatValue(value, isPercent, name) {
      if (name === 'Avg # Hldgs (funds)') {
        return this.isNumber(value) ? parseFloat(value).toFixed(0) : 'N/A'; // Always show as a whole number
      }
      if (this.isNumber(value)) {
        const formattedNumber = parseFloat(value).toFixed(2);
        return isPercent ? `${formattedNumber}%` : formattedNumber;
      }
      return value || 'N/A'; // Return 'N/A' if value is undefined or null
    },
    calculateDifference(current, proposal, isPercent, name) {
      if (name == 'Avg # Hldgs (funds)') {
        const num_difference = parseFloat(current) - parseFloat(proposal);
        return num_difference.toFixed(0)
      } else if (this.isNumber(current) && this.isNumber(proposal)) {
        const difference = parseFloat(current) - parseFloat(proposal);
        return isPercent ? `${difference.toFixed(2)}%` : difference.toFixed(2); // Append '%' if it's a percentage
      }
      return ''; // Return an empty string if either value is not a number
    },
    exportToPdf() {
      this.isExporting = true; // Show loading overlay

      const pdf = new jsPDF('l', 'pt', 'letter');
      const pageWidth = 792; // Standard Letter width
      const pageHeight = 612; // Standard Letter height
      const padding = 40; // Adjust this for desired left/right padding
      const imgWidth = pageWidth - 2 * padding; // Reduce width to account for padding
      let position = 80; // Increased for top padding after header
      let currentPage = 1;

      const buttons = document.querySelectorAll('.no-print');
      buttons.forEach(button => button.style.display = 'none'); // Hide buttons before export

      const headerElement = document.querySelector('.header-section'); // Select the headers
      const areas = document.querySelectorAll('.area-container'); // Select all content areas

      const base64Logo = this.advisorStore.firmLogoBase64;

      // Set fallback dimensions
      let logoWidth = 100;
      let logoHeight = 0;
      const maxLogoHeight = 50;

      if (base64Logo) {
        logoHeight = maxLogoHeight;
        logoWidth = maxLogoHeight; // Adjust based on actual aspect ratio if needed
      }

      html2canvas(headerElement, { scale: 1 }).then((headerCanvas) => {
        const headerImgData = headerCanvas.toDataURL('image/png');
        const headerHeight = (headerCanvas.height * imgWidth) / headerCanvas.width;

        // Option 1: Add logo above the header
        if (base64Logo) {
          pdf.addImage(`data:image/png;base64,${base64Logo}`, 'PNG', pageWidth - padding - logoWidth, 20, logoWidth, logoHeight);
          pdf.addImage(headerImgData, 'PNG', padding, 20 + logoHeight + 10, imgWidth, headerHeight);
          position = 20 + logoHeight + 10 + headerHeight + 20;
        } else {
          pdf.addImage(headerImgData, 'PNG', padding, 20, imgWidth, headerHeight);
          position = 20 + headerHeight + 20;
        }

        const processAreas = (index) => {
          if (index >= areas.length) {
            buttons.forEach(button => button.style.display = ''); // Show buttons again
            this.isExporting = false; // Hide loading overlay
            pdf.save('analytics-overview.pdf'); // Save the PDF
            return;
          }

          const area = areas[index];

          html2canvas(area, { scale: 1 }).then((canvas) => {
            const areaHeight = (canvas.height * imgWidth) / canvas.width;

            // If the area won't fit on the current page, add a new page with headers
            if (position + areaHeight > pageHeight - 10) {
              pdf.addPage();
              position = 30;
              currentPage++;

              // Re-add logo and headers at the top of the new page
              if (base64Logo) {
                pdf.addImage(`data:image/png;base64,${base64Logo}`, 'PNG', pageWidth - padding - logoWidth, 20, logoWidth, logoHeight);
                pdf.addImage(headerImgData, 'PNG', padding, 20 + logoHeight + 10, imgWidth, headerHeight);
                position = 20 + logoHeight + 10 + headerHeight + 20;
              } else {
                pdf.addImage(headerImgData, 'PNG', padding, 20, imgWidth, headerHeight);
                position = 20 + headerHeight + 20;
              }
            }

            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', padding, position, imgWidth, areaHeight);
            position += areaHeight + 10;

            processAreas(index + 1);
          });
        };

        processAreas(0);
      }).catch((error) => {
        console.error("Error exporting PDF:", error);
        this.isExporting = false;
      });
    },
  },
};
</script>

<style scoped>
p.heading {
  font-size: 1.2rem;
  font-weight: bold;
}

/* Hide elements in exported PDF */
@media print {
  .no-print {
    display: none !important;
  }
}

/* Area container styling */
.area-container {
  display: block;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
}

/* Special styling for PDF export mode */
.for-pdf-export .area-container {
  margin-bottom: 0;
}

/* Temporary container for PDF generation */
.area-pdf-container {
  background: white;
  padding: 20px;
  width: 612px; /* Letter width */
  box-sizing: border-box;
  font-family: inherit;
  font-size: 9pt; /* Reduced font size for PDF output */
}

/* Control font sizes in PDF more explicitly */
.area-pdf-container .heading {
  font-size: 12pt;
}

.area-pdf-container .tw-text-2xl {
  font-size: 14pt; 
}

.area-pdf-container .has-text-weight-bold,
.area-pdf-container strong {
  font-size: 9pt;
}

/* Improved header section with better spacing */
.header-section {
  font-size: 12pt;
  font-weight: bold;
  margin-bottom: 1.5rem !important;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #ccc;
}

/* Added padding for exported elements */
.for-pdf-export {
  padding: 15px;
}

/* Loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-content {
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  text-align: center;
}

.loading-content i {
  color: #48c774; /* Green color to match the PDF button */
  margin-bottom: 10px;
}
</style>
